import { useCallback, useEffect, useRef } from 'react';

import { useViewportSizeEffect } from 'hooks/viewport/useViewportSizeEffect';
import { transition } from 'theme/theme';

import { TabsItem } from './Item/TabsItem';
import { Tab } from './Tab';
import { TabsContainer, TabsMarker, TabsUl } from './Tabs.styled';

type Props<TValue extends string> = {
  tabs: Tab<TValue>[] | readonly Tab<TValue>[];
  value: TValue | undefined;
  onSelectTab: (value: TValue) => void;
  layout: 'vertical-on-desktop' | 'horizontal';
  variant: 'background' | 'marker' | 'compact-marker';
};

export function Tabs<T extends string>({
  tabs,
  value,
  onSelectTab,
  layout,
  variant,
}: Props<T>) {
  const tabsContainerRef = useRef<HTMLUListElement>(null);
  const markerRef = useRef<HTMLDivElement>(null);
  const selectedTabIndex = value
    ? tabs.findIndex((tab) => tab.value === value)
    : -1;

  const hasMarker = variant === 'marker' || variant === 'compact-marker';

  const moveMarker = useCallback(
    (animate?: boolean) => {
      if (!hasMarker) return;

      const tabsContainer = tabsContainerRef.current;
      const marker = markerRef.current;

      if (!tabsContainer || !marker || selectedTabIndex === -1) return;

      const tabNode = tabsContainer.childNodes[selectedTabIndex];
      if (!tabNode) return;

      const { offsetLeft, offsetWidth, offsetTop, offsetHeight } =
        tabNode as HTMLDivElement;

      marker.style.width = `${offsetWidth}px`;
      marker.style.left = `${offsetLeft - tabsContainer.scrollLeft}px`;
      marker.style.top = `${
        offsetTop + offsetHeight - tabsContainer.scrollTop
      }px`;

      if (!animate) {
        marker.style.transition = 'none';
      }

      setTimeout(() => {
        marker.style.transition = transition;
      }, 0);
    },
    [hasMarker, selectedTabIndex],
  );

  useEffect(() => {
    moveMarker(true);
  }, [moveMarker]);

  useViewportSizeEffect(() => {
    moveMarker(true);
  });

  return (
    <TabsContainer data-qa-id="tabs">
      <TabsUl
        ref={tabsContainerRef}
        onScroll={() => moveMarker(false)}
        $layout={layout}
        $variant={variant}
      >
        {tabs.map((tab) => (
          <TabsItem
            key={tab.value}
            isSelected={tab.value === value}
            onChange={() => onSelectTab(tab.value)}
            variant={variant}
            tabButtonId={tab.tabButtonId}
            tabPanelId={tab.tabPanelId}
            qaId={tab.qaId}
          >
            {tab.label}
          </TabsItem>
        ))}
      </TabsUl>

      {hasMarker && <TabsMarker ref={markerRef} />}
    </TabsContainer>
  );
}
